<template>
  <div class="checkout-return-container">
    <div class="inner-container">
      <h1 class="title">Subscription Status</h1>
      <div v-if="loading">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="subtitle mt-4">Verifying your subscription...</p>
      </div>
      <div v-else-if="success">
        <h2 class="text-green-600">Subscription Successful!</h2>
        <p class="subtitle mt-2">Thank you for subscribing. You will be redirected to the dashboard shortly.</p>
      </div>
      <div v-else>
        <h2 class="text-red-600">Subscription Failed or Not Verified.</h2>
        <p class="subtitle mt-2">There was an issue verifying your subscription. Please contact support or try again.</p>
        <p v-if="errorMessage" class="error-message mt-2">{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';
import { useStore } from 'vuex';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { getAccessTokenSilently } = useAuth0();

const loading = ref(true);
const success = ref(false);
const errorMessage = ref(null);

onMounted(async () => {
  const sessionId = route.query.session_id;

  if (!sessionId) {
    errorMessage.value = "No session ID found in URL. Cannot verify subscription.";
    loading.value = false;
    return;
  }

  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    // Call backend to verify the session
    const response = await axios.post(`${process.env.VUE_APP_CBE_BASE_URL}/stripe/verify-checkout-session`, {
      sessionId: sessionId,
    }, { headers });

    if (response.data.verified) {
      success.value = true;
      store.commit('CLEAR_BILLING_INFO_FORM');
      // By clearing the authentication, we ensure the router guard
      // will fetch fresh user data, including their new "Active" status.
      store.commit('CLEAR_AUTHENTICATION');

      // Redirect after 3 seconds using the router.
      setTimeout(() => {
        router.push({ name: 'GetStarted' });
      }, 3000);
    } else {
      errorMessage.value = response.data.message || "Verification failed.";
      success.value = false;
    }
  } catch (error) {
    console.error("Error verifying checkout session:", error);
    errorMessage.value = error.response?.data?.detail || "An unexpected error occurred during verification.";
    success.value = false;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
</style>